import { USER_LOCATION_MARKER_SOURCE, NAVIGATION_CONTROLLER_SOURCE } from './map-scripts.generated';
// Self-contained so browser maps and native WebViews render the same location icon.
export function createUserLocationMarker(map, mapbox, iconUrl = '/images/03_travel/point_value/location-direction-250.png') {
    const el = document.createElement('div');
    el.setAttribute('aria-label', 'Ваше местоположение');
    el.setAttribute('role', 'img');
    Object.assign(el.style, { width: '85px', height: '85px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' });
    const icon = document.createElement('img');
    icon.src = iconUrl;
    icon.width = 250;
    icon.height = 250;
    icon.alt = '';
    icon.draggable = false;
    // The PNG's location circle is at (112.5, 145), and its direction points NE.
    Object.assign(icon.style, { width: '100%', height: '100%', objectFit: 'contain', transformOrigin: '45% 58%', pointerEvents: 'none' });
    el.appendChild(icon);
    const marker = new mapbox.Marker({ element: el, rotationAlignment: 'map' });
    let added = false;
    const setHeading = (heading) => {
        var _a;
        const known = typeof heading === 'number' && Number.isFinite(heading) && heading >= 0;
        Object.assign(icon.style, {
            transform: `translate(5%, -8%) rotate(${known ? -45 : 0}deg)`,
            clipPath: known ? 'none' : 'circle(36% at 45% 58%)',
        });
        (_a = marker.setRotation) === null || _a === void 0 ? void 0 : _a.call(marker, known ? heading % 360 : 0);
    };
    setHeading(null);
    return {
        update(coordinate, heading) {
            marker.setLngLat(coordinate);
            if (!added) {
                marker.addTo(map);
                added = true;
            }
            setHeading(heading);
        },
        setHeading,
        setVisible(visible) { el.style.display = visible ? 'flex' : 'none'; },
        remove() { marker.remove(); },
    };
}
export const USER_LOCATION_MAP_SCRIPT = `(()=>{const createUserLocationMarker=(${USER_LOCATION_MARKER_SOURCE});
let mapUserLocationMarker=null;
window.updateMapUserLocation=location=>{
  if(!location){if(mapUserLocationMarker)mapUserLocationMarker.remove();mapUserLocationMarker=null;return;}
  if(!mapUserLocationMarker)mapUserLocationMarker=createUserLocationMarker(map,mapboxgl);
  mapUserLocationMarker.update([location.longitude,location.latitude],location.heading);
};})();`;
// Uses only its arguments so the same controller can run inside the native
// WebView document and on the browser map.
export function createNavigationMapController(map, mapbox, userMarkerFactory = createUserLocationMarker) {
    let route = null;
    let fix = null;
    let progress = null;
    let navigating = false;
    let restoringManualCamera = Boolean(map.__trackmeCameraManual);
    let following = !restoringManualCamera;
    let traffic = false;
    let user = null;
    let destination = null;
    let originalCamera = null;
    let cameraMovedByUser = false;
    const padding = () => {
        const height = map.getContainer ? map.getContainer().clientHeight || 600 : 600;
        return { top: Math.min(110, height * 0.18), bottom: Math.min(210, height * 0.35), left: 20, right: 20 };
    };
    const feature = (coordinates, passed) => ({ type: 'Feature', properties: { passed }, geometry: { type: 'LineString', coordinates } });
    const drawTraffic = () => {
        if (!map.isStyleLoaded())
            return;
        if (!traffic) {
            if (map.getLayer('navigation-traffic'))
                map.removeLayer('navigation-traffic');
            if (map.getSource('navigation-traffic'))
                map.removeSource('navigation-traffic');
            return;
        }
        if (!map.getSource('navigation-traffic'))
            map.addSource('navigation-traffic', { type: 'vector', url: 'mapbox://mapbox.mapbox-traffic-v1' });
        if (!map.getLayer('navigation-traffic'))
            map.addLayer({
                id: 'navigation-traffic', type: 'line', source: 'navigation-traffic', 'source-layer': 'traffic', minzoom: 5,
                filter: ['in', ['get', 'congestion'], ['literal', ['low', 'moderate', 'heavy', 'severe']]],
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: {
                    'line-color': ['match', ['get', 'congestion'], 'low', '#35A853', 'moderate', '#F2B134', 'heavy', '#E95040', 'severe', '#9E172B', '#808080'],
                    'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1, 12, 3, 17, 6],
                    'line-offset': ['interpolate', ['linear'], ['zoom'], 5, 0, 12, 2, 17, 5],
                },
            }, map.getLayer('navigation-route-outline') ? 'navigation-route-outline' : undefined);
    };
    const draw = () => {
        if (!map.isStyleLoaded())
            return;
        drawTraffic();
        if (!route)
            return;
        if (!map.getSource('navigation-route')) {
            map.addSource('navigation-route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
            map.addLayer({ id: 'navigation-route-outline', type: 'line', source: 'navigation-route', layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': '#FFFFFF', 'line-width': 11, 'line-opacity': 0.96 } });
            map.addLayer({ id: 'navigation-route-line', type: 'line', source: 'navigation-route', layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': ['case', ['get', 'passed'], '#9CABAE', '#3F7A59'], 'line-width': 7 } });
        }
        const features = progress && navigating && progress.offRoute < 100 ? [
            feature([...route.coordinates.slice(0, progress.segment + 1), progress.coordinate], true),
            feature([progress.coordinate, ...route.coordinates.slice(progress.segment + 1)], false),
        ] : [feature(route.coordinates, false)];
        map.getSource('navigation-route').setData({ type: 'FeatureCollection', features });
        if (!destination) {
            const el = document.createElement('div');
            el.textContent = '🏁';
            Object.assign(el.style, { width: '34px', height: '34px', fontSize: '23px', textAlign: 'center', lineHeight: '34px', background: '#FFF', border: '2px solid #3F7A59', borderRadius: '50%', boxShadow: '0 2px 8px #0004' });
            destination = new mapbox.Marker({ element: el }).setLngLat(route.coordinates[route.coordinates.length - 1]).addTo(map);
        }
        destination.setLngLat(route.coordinates[route.coordinates.length - 1]);
        if (fix) {
            if (!user) {
                user = userMarkerFactory(map, mapbox);
            }
            const heading = fix.heading != null && fix.heading >= 0 ? fix.heading : (progress === null || progress === void 0 ? void 0 : progress.bearing) || 0;
            const coordinate = progress && progress.offRoute < Math.max(35, fix.accuracy * 2) ? progress.coordinate : fix.coordinate;
            user.update(coordinate, heading);
            if (navigating && following)
                map.easeTo({ center: coordinate, bearing: heading, pitch: 50, zoom: 17, padding: padding(), duration: 800, essential: true });
        }
    };
    const pauseFollow = () => { following = false; if (route)
        cameraMovedByUser = true; };
    const onManualMove = (event) => { if (event.originalEvent)
        pauseFollow(); };
    map.on('dragstart', pauseFollow);
    map.on('movestart', onManualMove);
    map.on('style.load', draw);
    const clear = () => {
        route = null;
        fix = null;
        progress = null;
        navigating = false;
        traffic = false;
        drawTraffic();
        user === null || user === void 0 ? void 0 : user.remove();
        user = null;
        destination === null || destination === void 0 ? void 0 : destination.remove();
        destination = null;
        for (const id of ['navigation-route-line', 'navigation-route-outline'])
            if (map.getLayer(id))
                map.removeLayer(id);
        if (map.getSource('navigation-route'))
            map.removeSource('navigation-route');
        if (originalCamera) {
            map.easeTo({ ...(cameraMovedByUser ? {} : originalCamera), padding: { top: 0, bottom: 0, left: 0, right: 0 }, duration: 400 });
            originalCamera = null;
        }
        cameraMovedByUser = false;
    };
    return {
        setRoute(next, fit = true) {
            if (!next) {
                clear();
                return;
            }
            if (!originalCamera)
                originalCamera = { center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() };
            route = next;
            progress = null;
            draw();
            if (fit) {
                const bounds = new mapbox.LngLatBounds();
                next.coordinates.forEach(point => bounds.extend(point));
                map.fitBounds(bounds, { padding: padding(), maxZoom: 16, pitch: 0, bearing: 0, duration: 600 });
            }
        },
        update(next) {
            if (!navigating && next.navigating && !restoringManualCamera) {
                following = true;
                map.__trackmeCameraManual = false;
            }
            restoringManualCamera = false;
            fix = next.fix;
            progress = next.progress;
            navigating = next.navigating;
            draw();
        },
        setTraffic(enabled) { traffic = enabled; drawTraffic(); },
        recenter() { following = true; map.__trackmeCameraManual = false; draw(); },
        clear,
        destroy() { map.off('dragstart', pauseFollow); map.off('movestart', onManualMove); map.off('style.load', draw); clear(); },
    };
}
export const NAVIGATION_MAP_SCRIPT = `const createUserLocationMarker=(${USER_LOCATION_MARKER_SOURCE});
const navigationMap=(${NAVIGATION_CONTROLLER_SOURCE})(map,mapboxgl,createUserLocationMarker);
window.setNavigationRoute=(route,fit)=>navigationMap.setRoute(route,fit);
window.updateNavigationPosition=state=>navigationMap.update(state);
window.recenterNavigation=()=>navigationMap.recenter();
window.setNavigationTraffic=enabled=>navigationMap.setTraffic(enabled);`;
